/**
 * Les notifications de la cloche, calculées à partir des chantiers : pas de
 * table à tenir, rien à marquer « lu » côté serveur. Ce qui demande une action
 * reste affiché tant qu'elle n'est pas faite ; ce qui informe (une validation)
 * disparaît au bout de 48 heures.
 */
import type { Utilisateur } from '@/shared/auth/types';
import type { ResumeChantier, ResumeTicket } from '@/shared/donnees';
import { enRetard } from '@/shared/referentiel/chantier';
import { joursEntre } from '@/shared/referentiel/conformite';

export interface Notification {
  id: string;
  ton: 'danger' | 'attention' | 'succes' | 'info';
  titre: string;
  detail: string;
  lien: string;
  /** Pour trier et dire « il y a… » ; absent pour un état durable (un retard). */
  quand: string | null;
}

const GRAVITE: Record<Notification['ton'], number> = {
  danger: 0,
  attention: 1,
  info: 2,
  succes: 3,
};

export function notifications(
  chantiers: readonly ResumeChantier[],
  utilisateur: Utilisateur,
  jour: string,
  maintenant = new Date(),
  tickets: readonly ResumeTicket[] = []
): Notification[] {
  const liste: Notification[] = [];

  /* Le SAV (S8) : ce qui est à reprendre ou commence pour le partenaire ;
     l'urgent à planifier et le résolu récent pour le bureau. */
  for (const t of tickets) {
    if (utilisateur.role === 'partenaire') {
      if (t.partenaireId !== utilisateur.partenaireId) continue;
      const lien = `/mes-etapes/${t.id}`;
      if (t.statut === 'rouvert') {
        liste.push({
          id: `sav-rouvert-${t.id}`,
          ton: 'danger',
          titre: `SAV à reprendre : ${t.objet}`,
          detail: `${t.entrepriseNom} — « ${t.motifReouverture ?? 'voir le motif'} »`,
          lien,
          quand: null,
        });
      } else if (t.statut === 'planifie' && t.debutPrevu !== null) {
        const dans = joursEntre(jour, t.debutPrevu);
        if (dans === 0 || dans === 1) {
          liste.push({
            id: `sav-debut-${t.id}`,
            ton: t.priorite === 'urgente' ? 'attention' : 'info',
            titre: `${dans === 0 ? 'Aujourd’hui' : 'Demain'} : SAV — ${t.objet}`,
            detail: `${t.entrepriseNom} · ${t.commune} (${t.departement})`,
            lien,
            quand: null,
          });
        }
      }
      continue;
    }
    const lien = `/sav/${t.id}`;
    if (t.priorite === 'urgente' && (t.statut === 'a-planifier' || t.statut === 'rouvert')) {
      liste.push({
        id: `sav-urgent-${t.id}`,
        ton: 'danger',
        titre: `SAV urgent : ${t.objet}`,
        detail: `${t.entrepriseNom} · ${t.reference} · à planifier`,
        lien,
        quand: t.ouvertLe,
      });
    }
    if (
      t.statut === 'resolu' &&
      t.resoluLe &&
      maintenant.getTime() - new Date(t.resoluLe).getTime() < 48 * 3_600_000
    ) {
      liste.push({
        id: `sav-resolu-${t.id}`,
        ton: 'succes',
        titre: `SAV résolu : ${t.objet}`,
        detail: `${t.entrepriseNom}${t.resoluPar ? ` · par ${t.resoluPar}` : ''}`,
        lien,
        quand: t.resoluLe,
      });
    }
  }

  for (const c of chantiers) {
    for (const e of c.etapes) {
      if (utilisateur.role === 'partenaire') {
        if (e.partenaireId !== utilisateur.partenaireId) continue;
        const lien = `/mes-etapes/${e.id}`;
        if (e.statut === 'rouverte') {
          liste.push({
            id: `rouverte-${e.id}`,
            ton: 'danger',
            titre: `À reprendre : ${e.libelle}`,
            detail: `${c.entrepriseNom} — « ${e.motifReouverture ?? 'voir le motif'} »`,
            lien,
            quand: null,
          });
        } else if (
          e.statut === 'validee' &&
          e.valideeLe &&
          maintenant.getTime() - new Date(e.valideeLe).getTime() < 48 * 3_600_000
        ) {
          liste.push({
            id: `validee-${e.id}`,
            ton: 'succes',
            titre: `Validée par le bureau : ${e.libelle}`,
            detail: c.entrepriseNom,
            lien,
            quand: e.valideeLe,
          });
        } else if (e.statut === 'planifiee' && e.debutPrevu !== null) {
          const dans = joursEntre(jour, e.debutPrevu);
          if (dans === 0 || dans === 1) {
            liste.push({
              id: `debut-${e.id}`,
              ton: 'info',
              titre: `${dans === 0 ? 'Aujourd’hui' : 'Demain'} : ${e.libelle}`,
              detail: `${c.entrepriseNom} · ${c.commune} (${c.departement})`,
              lien,
              quand: null,
            });
          }
        }
        continue;
      }

      const lien = `/chantiers/${c.id}`;
      if (e.reserve) {
        liste.push({
          id: `reserve-${e.id}`,
          ton: 'attention',
          titre: `Réserve : ${e.libelle}`,
          detail: `${c.entrepriseNom} — « ${e.reserve.texte} »`,
          lien,
          quand: e.reserve.signaleeLe,
        });
      }
      if (enRetard(e, jour)) {
        liste.push({
          id: `retard-${e.id}`,
          ton: 'danger',
          titre: `En retard : ${e.libelle}`,
          detail: `${c.entrepriseNom}${e.partenaireNom ? ` · ${e.partenaireNom}` : ''}`,
          lien,
          quand: null,
        });
      }
      /* Le bureau valide (S10) : ce que les partenaires lui envoient l'attend. */
      if (e.statut === 'a-valider') {
        liste.push({
          id: `a-valider-${e.id}`,
          ton: 'info',
          titre: `À valider : ${e.libelle}`,
          detail: `${c.entrepriseNom}${e.partenaireNom ? ` · envoyée par ${e.partenaireNom}` : ''}`,
          lien,
          quand: e.soumiseLe,
        });
      }
    }
  }

  return liste.sort(
    (a, b) => GRAVITE[a.ton] - GRAVITE[b.ton] || (b.quand ?? '').localeCompare(a.quand ?? '')
  );
}
